import { useCallback, useEffect, useMemo, useState } from 'react'

const matchesSearch = (option, query) => {
	const q = String(query || '')
		.trim()
		.toLowerCase()
	if (!q) return true

	const haystack = [
		option?.label,
		option?.hint,
		option?.meta,
		option?.searchText,
	]
		.filter(Boolean)
		.join(' ')
		.toLowerCase()

	return haystack.includes(q)
}

export function useDropdownFilter({
	options = [],
	selected = [],
	filterKey,
	onChangeKey,
	searchable = false,
} = {}) {
	const [isOpen, setIsOpen] = useState(false)
	const [searchQuery, setSearchQuery] = useState('')

	const isActive = selected.length > 0

	useEffect(() => {
		if (!isOpen) setSearchQuery('')
	}, [isOpen])

	const selectedLabels = useMemo(() => {
		const map = new Map(options.map(o => [o.value, o.label]))
		return selected.map(v => ({ value: v, label: map.get(v) || String(v) }))
	}, [options, selected])

	const visibleOptions = useMemo(() => {
		if (!searchable) return options
		return options.filter(opt => matchesSearch(opt, searchQuery))
	}, [options, searchable, searchQuery])

	const toggleOpen = useCallback(() => {
		setIsOpen(v => !v)
	}, [])

	const close = useCallback(() => {
		setIsOpen(false)
	}, [])

	const toggleValue = useCallback(
		value => {
			const exists = selected.includes(value)
			const next = exists
				? selected.filter(v => v !== value)
				: [...selected, value]
			onChangeKey?.(filterKey, next)
		},
		[selected, onChangeKey, filterKey],
	)

	const removeValue = useCallback(
		(value, e) => {
			e?.stopPropagation?.()
			toggleValue(value)
		},
		[toggleValue],
	)

	return {
		isOpen,
		isActive,
		selectedLabels,
		options,
		visibleOptions,
		selected,
		searchQuery,
		setSearchQuery,
		toggleOpen,
		close,
		toggleValue,
		removeValue,
		setIsOpen,
	}
}
